import * as React from "react";
import { useForm } from "@mantine/form";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { Fab, Stack } from "@mui/material";
import { AddCircleOutlineRounded } from "@mui/icons-material";
import { useDisclosure } from "@mantine/hooks";
import { createCollectedInformation } from "../../services/collectedinformation.service";
import {
  InformationCreationModel,
  InformationDetailModel,
} from "../../models/information.model";

interface CreateCollectableInformationProps {
  onSaveSuccess: (info: InformationDetailModel) => any;
}

export default function CreateCollectableInformation({
  onSaveSuccess,
}: CreateCollectableInformationProps) {
  const [open, setOpen] = React.useState(false);
  const [isSubmitting, { open: setSubmitting, close: completeSubmitting }] =
    useDisclosure(false);

  const form = useForm({
    initialValues: {
      propertyLabel: "",
      propertyDescription: "",
    },

    validate: {
      propertyLabel: (value: any) => {
        if (!value) return "Name is required";
        if (/[^a-zA-Z0-9 ]/.test(value)) return "No special characters allowed";
        return null;
      },
      propertyDescription: (value: any) => {
        if (!value) return "Description is required";
        if (/[^a-zA-Z0-9 ]/.test(value)) return "No special characters allowed";
        return null;
      },
    },

    validateInputOnChange: true, // <-- triggers validation on every keystroke
  });

  const handleClickOpen = () => setOpen(true);

  const handleClose = () => {
    if (!isSubmitting) {
      form.reset(); // reset form when dialog closes
      setOpen(false);
      return;
    }
    console.log("Cannot close the from is being submitted");
  };

  const handleSubmit = async (values: typeof form.values) => {
    setSubmitting();
    try {
      const response = await createCollectedInformation(
        values as InformationCreationModel,
      );
      handleClose();
      onSaveSuccess(response);
    } catch (error) {
      console.error("Form submitting error", error);
      alert("Unable to save collectable data, please try again !");
    } finally {
      completeSubmitting();
    }
  };

  return (
    <React.Fragment>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>New Collectable Data</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2, fontSize: 14 }}>
            Create new collectable data fields that your agents can use to
            gather information from users. This data can help you generate leads
            and grow your sales.
          </DialogContentText>

          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack spacing={2}>
              <TextField
                autoFocus
                fullWidth
                label="Name"
                placeholder="E.g Customer Address"
                {...form.getInputProps("propertyLabel")}
                error={!!form.errors.propertyLabel}
                helperText={form.errors.propertyLabel}
              />
              <TextField
                fullWidth
                label="Description"
                placeholder="E.g The full address of the customer along with pincode"
                {...form.getInputProps("propertyDescription")}
                error={!!form.errors.propertyDescription}
                helperText={form.errors.propertyDescription}
              />
            </Stack>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            onClick={() => form.onSubmit(handleSubmit)()}
            variant="contained"
            color="success"
            disabled={form.isDirty() && !form.isValid()}
            loading={isSubmitting}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Fab
        onClick={handleClickOpen}
        color="success"
        variant="extended"
        sx={{ position: "fixed", bottom: 24, right: 24 }}
      >
        <AddCircleOutlineRounded sx={{ mr: 1 }} />
        Add Collectable Data
      </Fab>
    </React.Fragment>
  );
}
